import React from "react";
import {Box} from "@mui/material";
import {TFunction, useTranslation} from "react-i18next";
import {EetsDeviceHistoryEntry, EetsDeviceHistoryType} from "../../../../../app/api/model/eetsDevice";
import {formatTimestamp} from "../../../../../utils/datetime";
import {hasJsonStructure} from "../../helpers";
import {DeviceHistorySeverity} from "../../../../../app/api/model/deviceHistory";
import {EMPTY_UI} from "../../../../../variables";

export const SUPPORTED_TYPES = [
    EetsDeviceHistoryType.DEV_DEREGISTER,
    EetsDeviceHistoryType.DEV_REGISTERED,
    EetsDeviceHistoryType.DEV_CREATED,
    EetsDeviceHistoryType.DEV_UPDATED,
    EetsDeviceHistoryType.DEV_CANCEL,
    EetsDeviceHistoryType.DEV_SCHEDULE_CANC,
];

export interface EetsDeviceHistoryInformationContentProps {
    entry: EetsDeviceHistoryEntry;
}

enum DefaultInfromationKeys {
    ORGANIZATION_ID = "organizationId",
    LEO_ID = "leoId",
    LEO_ART = "leoArt",
    OBU_ID = "obuId",
    T_4_E_CONTRACT_ID = "t4eContractId",
    CANCEL_DATE = "cancelDate",
    CANCEL_REASON = "cancelReason",
    VIN = "vin",
    LICENSE_PLATE = "licensePlate",
}

type DefaultInformation = {
    [DefaultInfromationKeys.ORGANIZATION_ID]: string;
    [DefaultInfromationKeys.LEO_ID]: string;
    [DefaultInfromationKeys.LEO_ART]: string;
    [DefaultInfromationKeys.OBU_ID]: string;
    [DefaultInfromationKeys.T_4_E_CONTRACT_ID]: string;
    [DefaultInfromationKeys.CANCEL_DATE]: string;
    [DefaultInfromationKeys.CANCEL_REASON]: string;
    [DefaultInfromationKeys.VIN]: string;
    [DefaultInfromationKeys.LICENSE_PLATE]: string;
};

interface ErrorJson {
    message: string;
    error: string;
}

const getContent = (value: string | undefined): string => value || EMPTY_UI;

function getDefaultInformation(info: DefaultInformation, t: TFunction) {
    const labelStyle = {fontWeight: "bold", display: "inline", marginRight: "5px"};
    return (
        <Box>
            <Box>
                <Box sx={labelStyle}>{`${t("generic_organizationId")}:`}</Box>
                {getContent(info.organizationId)}
            </Box>
            <Box>
                <Box sx={labelStyle}>{`${t("generic_leoId")}:`}</Box>
                {getContent(info.leoId)}
            </Box>
            <Box>
                <Box sx={labelStyle}>{`${t("eets_leoArticle")}:`}</Box>
                {getContent(info[DefaultInfromationKeys.LEO_ART])}
            </Box>
            <Box>
                <Box sx={labelStyle}>{`${t("eets_obuId")}:`}</Box>
                {getContent(info[DefaultInfromationKeys.OBU_ID])}
            </Box>
            <Box>
                <Box sx={labelStyle}>{`${t("eets_t4eContractId")}:`}</Box>
                {getContent(info[DefaultInfromationKeys.T_4_E_CONTRACT_ID])}
            </Box>
            {info[DefaultInfromationKeys.VIN] && (
                <Box>
                    <Box sx={labelStyle}>{`${t("eets_vin")}:`}</Box>
                    {getContent(info[DefaultInfromationKeys.VIN])}
                </Box>
            )}
            {info[DefaultInfromationKeys.LICENSE_PLATE] && (
                <Box>
                    <Box sx={labelStyle}>{`${t("eets_licensePlate")}:`}</Box>
                    {getContent(info[DefaultInfromationKeys.LICENSE_PLATE])}
                </Box>
            )}
            {info.cancelDate && (
                <>
                    <Box>
                        <Box sx={labelStyle}>{`${t("eetsDevice_canceledAt")}:`}</Box>
                        {`${formatTimestamp(info?.cancelDate)}`}
                    </Box>
                    <Box>
                        <Box sx={labelStyle}>{`${t("generic_cancelReason")}:`}</Box>
                        {getContent(info?.cancelReason)}
                    </Box>
                </>
            )}
        </Box>
    );
}
function getMessageInformation(info: ErrorJson, t: TFunction) {
    return (
        <Box>
            <Box>
                <Box sx={{fontWeight: "bold"}}>{`${t("generic_message")}`}</Box>
                <Box>{getContent(info?.message)}</Box>
                {info?.error && (
                    <>
                        <Box sx={{fontWeight: "bold"}}>{`${t("deviceHistorySeverity_error")}`}</Box>
                        <Box>{`${info?.error}`}</Box>
                    </>
                )}
            </Box>
        </Box>
    );
}

function EetsDeviceHistoryInformationContent({entry}: EetsDeviceHistoryInformationContentProps) {
    const {t} = useTranslation();

    let content: React.ReactNode;
    if (hasJsonStructure(entry.information)) {
        const info = JSON.parse(entry.information);

        if (entry.severity === DeviceHistorySeverity.ERROR) {
            content = getMessageInformation(info, t);
        } else {
            switch (entry.type) {
                case EetsDeviceHistoryType.DEV_DEREGISTER:
                case EetsDeviceHistoryType.DEV_REGISTERED:
                    content = getMessageInformation(info, t);
                    break;
                case EetsDeviceHistoryType.DEV_CREATED:
                case EetsDeviceHistoryType.DEV_UPDATED:
                case EetsDeviceHistoryType.DEV_CANCEL:
                case EetsDeviceHistoryType.DEV_SCHEDULE_CANC:
                default:
                    content = getDefaultInformation(info, t);
                    break;
            }
        }
    }
    return <>{content}</>;
}

export default EetsDeviceHistoryInformationContent;
